export default function PasadenaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Pasadena Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Pasadena travelers actually move helps us anticipate the passport urgencies that arise here. From Caltech conference cycles to Rose Bowl family trips, San Gabriel Valley travel operates on a distinct academic and cultural rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flask-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Caltech &amp; JPL Research Travel</h3>
                <p className="text-gray-400 text-xs">Academic conferences, international collaborations, field research</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Caltech and JPL together generate more international travel from Pasadena than any other single source. Faculty receive last-minute conference invitations. Graduate students earn research fellowships abroad. JPL mission partners require on-site visits to ESA facilities in Darmstadt, JAXA in Japan, and observatories in Chile. The common thread: these trips are often confirmed weeks before departure, not months.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Academic travel windows do not accommodate standard processing timelines. If you have a conference or collaboration trip on the calendar — even months out — renew now.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">LAX &amp; Burbank Airport Access</h3>
                <p className="text-gray-400 text-xs">Los Angeles International &amp; Hollywood Burbank Airport</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Pasadena residents have two major airport options: LAX (about 25 miles southwest) for the full range of international routes, and Burbank (about 10 miles northwest) for domestic and some regional flights. LAX's direct international connections to Tokyo, Seoul, Frankfurt, Paris, and Latin America make it the primary gateway for Pasadena's international travel community — but proximity to LAX also means more spontaneous international bookings, which creates expedited passport demand.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking any LAX international flight. Many destinations — especially in Asia and Europe — require six months validity beyond your return date.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-group-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">San Gabriel Valley Asian-American Community</h3>
                <p className="text-gray-400 text-xs">Family travel to East Asia, South Korea, the Philippines, and Southeast Asia</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The San Gabriel Valley — encompassing Arcadia, Temple City, San Gabriel, Alhambra, and Monterey Park — has one of the largest concentrations of Asian-Americans in the United States. Family travel to China, Taiwan, South Korea, and Southeast Asia peaks around Lunar New Year (January/February), summer holidays, and Chinese National Day/Golden Week (October). Many travelers also face passport urgency when elderly relatives abroad fall ill, requiring immediate emergency travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check all family members' passport expirations before Lunar New Year or Golden Week travel — these are the busiest travel windows of the year and passport agencies have the longest wait times.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">International Students &amp; Graduate Researchers</h3>
                <p className="text-gray-400 text-xs">Caltech students, exchange programs, visiting researchers</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Caltech enrolls hundreds of international students and visiting researchers who are U.S. green card holders or naturalized citizens requiring U.S. passports. Many apply for or renew passports on academic timelines — often discovering they need expedited processing because their study-abroad program, international conference, or field research trip is weeks away. First-time U.S. passport applicants at Caltech often need guidance through the acceptance clerk process.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (including newly naturalized citizens) must visit an acceptance clerk in person. We provide a personalized checklist and exact Pasadena-area locations so you get it right the first time.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-trophy-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Rose Bowl &amp; Huntington Library Visitors</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Pasadena hosts the Rose Bowl Game, Rose Parade, and the Huntington Library — attracting international visitors year-round. Pasadena families hosting international guests or traveling internationally for New Year celebrations often discover passport expirations in the fall. Start early — do not wait until December to discover a January travel problem.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}